import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HelpTip,
  ErrorText,
  ExclusivityCountdown,
  ProcessingProgress,
  cn,
  isProcessingEstimate,
  phaseDateRange,
  todayInZone,
} from '@dataroom/shared';
import type { DdPhaseSummary, RoomDocument } from '@dataroom/shared';
import {
  useDdRequestsOfPhases,
  useDdSummary,
  useProcessingEstimate,
  useRoomExclusivity,
  useRoomLois,
  useSellerQuestions,
  useSellerRoom,
} from '@/api/hooks';
import { overallProgress, phasePercent, phaseResolved } from '@/lib/checklist';
import {
  checklistLink,
  DUE_SOON_DAYS,
  dueLabel,
  dueSoon,
  questionsAwaitingAnswer,
  votesAwaitingOwner,
} from '@/lib/dashboard';
import { RequestItem } from './RequestChecklist';

/**
 * The cards of the seller room's dashboard (the room home, under the drop zone).
 * Each one reads only what the seller portal already shows elsewhere, through the
 * same hooks and so the same server gates: nothing here is a new data source.
 */

const linkClass =
  'rounded text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

function Stat({
  label,
  value,
  tone,
  hint,
}: {
  label: string;
  value: number;
  tone: 'filed' | 'check' | 'neutral';
  hint?: string;
}) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2',
        tone === 'filed' && 'border-st-filed-line bg-st-filed text-st-filed-fg',
        tone === 'check' && 'border-st-check-line bg-st-check text-st-check-fg',
        tone === 'neutral' && 'bg-surface-2 text-ink',
      )}
      title={hint}
    >
      <dt className="text-xs font-medium">{label}</dt>
      <dd className="text-xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

function Bar({ percent, label }: { percent: number; label: string }) {
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-muted"
      role="progressbar"
      aria-label={label}
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
    </div>
  );
}

/** What a phase row says about what is left, from the server's exact buckets. */
function phaseLeft(phase: DdPhaseSummary): { text: string; missing: boolean } {
  if (phase.missing > 0) return { text: `${phase.missing} still missing`, missing: true };
  if (phase.waitingOnReview > 0) {
    return { text: `${phase.waitingOnReview} waiting on review`, missing: false };
  }
  return { text: 'All resolved', missing: false };
}

/**
 * Progress: the whole checklist (covered / missing / waiting / not applicable and
 * resolved of total — optional items count as missing until provided or attested,
 * the server's buckets) and, per released phase, a bar, what is still missing and
 * the phase's dates. A phase opens the Checklist tab on its missing items.
 */
export function ProgressCard({ roomId }: { roomId: string }) {
  const summary = useDdSummary(roomId);
  const phases = summary.data ?? [];
  const progress = overallProgress(phases);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <CardTitle>
            Progress <HelpTip id="seller.dashboard.progress" className="ml-1" />
          </CardTitle>
          <Link to={checklistLink(roomId)} className={linkClass}>
            Open the full checklist
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          What the deal team has asked for. Items update as your files are matched to them.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {summary.isPending ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : summary.error ? (
          <ErrorText error={summary.error} />
        ) : phases.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No documents have been requested yet. Upload what you have — it is filed in the data
            room either way, and it will count toward requests as they arrive.
          </p>
        ) : (
          <>
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4" aria-label="Checklist status">
              <Stat label="Covered" value={progress.covered} tone="filed" />
              <Stat
                label="Missing"
                value={progress.missing}
                tone={progress.missing > 0 ? 'check' : 'neutral'}
                hint="Required and optional items with no document yet"
              />
              <Stat
                label="Waiting on review"
                value={progress.waitingOnReview}
                tone="neutral"
                hint="The AI matched a document to these items; the deal team confirms it"
              />
              <Stat
                label="Not applicable"
                value={progress.notApplicable}
                tone="neutral"
                hint="You confirmed these don’t apply to your company"
              />
            </dl>
            <div className="space-y-1">
              <p className="text-sm font-medium">
                {progress.resolved} of {progress.total} items resolved
              </p>
              <Bar percent={progress.percent} label="Items resolved" />
            </div>
            <ul className="space-y-2" aria-label="Progress by phase">
              {phases.map((phase) => {
                const left = phaseLeft(phase);
                const range = phaseDateRange(phase);
                return (
                  // The whole row opens the phase: the link's ::after covers the row, so the
                  // "?" beside the dates (a button, which cannot sit inside a link) is a
                  // later sibling and paints above it.
                  <li
                    key={phase.phaseId ?? 'ungrouped'}
                    className="relative space-y-1.5 rounded-md border px-3 py-2.5 hover:bg-surface-2"
                  >
                    <Link
                      to={checklistLink(roomId, {
                        phaseId: phase.phaseId,
                        missingOnly: phase.missing > 0,
                      })}
                      className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 rounded-md after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
                    >
                      <span className="text-sm font-semibold">
                        {phase.name ?? 'Other requested items'}
                      </span>
                      <span
                        className={cn(
                          'text-xs font-medium',
                          left.missing ? 'text-brass' : 'text-ink-2',
                        )}
                      >
                        {left.text}
                      </span>
                    </Link>
                    {range ? (
                      <span className="flex items-center gap-1 text-xs text-ink-2">
                        {range}
                        <HelpTip id="seller.dashboard.phase-dates" />
                      </span>
                    ) : null}
                    <Bar
                      percent={phasePercent(phase)}
                      label={`${phase.name ?? 'Other requested items'} resolved`}
                    />
                    <span className="block text-xs text-muted-foreground">
                      {phaseResolved(phase)} of {phase.total} resolved
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Due soon: what is still missing and overdue or due in the next week (an item's
 * own due date, else its phase's), earliest first, each with the per-item upload /
 * link / "doesn't apply" actions of the checklist.
 */
export function DueSoonCard({
  roomId,
  documents,
}: {
  roomId: string;
  documents: readonly RoomDocument[];
}) {
  const summary = useDdSummary(roomId);
  const room = useSellerRoom(roomId);
  const phasesWithMissing = (summary.data ?? []).filter((p) => p.missing > 0);
  const all = useDdRequestsOfPhases(roomId, phasesWithMissing);
  const today = todayInZone(room.data?.timezone ?? 'UTC');
  const due = dueSoon(all.requests, today);
  const docsById = new Map(documents.map((d) => [d.id, d] as const));
  const loading = summary.isPending || (phasesWithMissing.length > 0 && all.isPending);
  const error = summary.error ?? all.error;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-1.5">
          <CardTitle>Due soon</CardTitle>
          <HelpTip id="seller.dashboard.due-soon" />
        </div>
        <p className="text-sm text-muted-foreground">
          Items still missing that are overdue or due within {DUE_SOON_DAYS} days.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <ErrorText error={error} />
        ) : due.items.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing is overdue or due in the next {DUE_SOON_DAYS} days.
          </p>
        ) : (
          <>
            <ul className="divide-y rounded-md border">
              {due.items.map((item) => (
                <RequestItem
                  key={item.req.id}
                  roomId={roomId}
                  req={item.req}
                  docsById={docsById}
                  note={
                    <p
                      className={cn(
                        'text-xs font-medium',
                        item.overdue ? 'text-destructive' : 'text-ink-2',
                      )}
                    >
                      {dueLabel(item)}
                    </p>
                  }
                />
              ))}
            </ul>
            {due.total > due.items.length ? (
              <p className="mt-2 flex flex-wrap items-center gap-x-3 text-xs text-muted-foreground">
                <span>
                  Showing {due.items.length} of {due.total}.
                </span>
                <Link
                  to={checklistLink(roomId, { missingOnly: true })}
                  className="font-medium text-primary hover:underline"
                >
                  See every missing item
                </Link>
              </p>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function WaitRow({ to, count, children }: { to: string; count: number; children: ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5 text-sm hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="min-w-0">{children}</span>
        <span className="shrink-0 rounded-full border border-st-check-line bg-st-check px-2 py-0.5 text-xs font-semibold tabular-nums text-st-check-fg">
          {count}
        </span>
      </Link>
    </li>
  );
}

/**
 * Waiting on you: buyer questions with no answer yet, documents to file by hand and,
 * for the OWNERS only, the votes they have yet to cast. Nothing is asked of a
 * seller's-team member about votes, and the LOI sources are not even requested for
 * them (the hooks are disabled), so no LOI data reaches their browser.
 */
export function WaitingOnYouCard({
  roomId,
  isOwner,
  documents,
  documentsPending,
}: {
  roomId: string;
  isOwner: boolean;
  /** The room listing: its `filingStatus` (served to the seller side with the processing step). */
  documents: readonly RoomDocument[];
  documentsPending: boolean;
}) {
  const questions = useSellerQuestions(roomId);
  const lois = useRoomLois(roomId, isOwner);
  const exclusivity = useRoomExclusivity(isOwner ? roomId : undefined);

  const toAnswer = questionsAwaitingAnswer(questions.data?.questions);
  const toFile = documents.filter((d) => d.filingStatus === 'needs_filing').length;
  const couldNotFile = documents.filter((d) => d.filingStatus === 'failed').length;
  const votes = isOwner ? votesAwaitingOwner(lois.data, exclusivity.data) : 0;
  const nothing = toAnswer + toFile + couldNotFile + votes === 0;
  const loading =
    questions.isPending ||
    documentsPending ||
    (isOwner && (lois.isPending || exclusivity.isPending));
  const base = `/rooms/${roomId}`;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-1.5">
          <CardTitle>Waiting on you</CardTitle>
          <HelpTip id="seller.dashboard.waiting-on-you" />
        </div>
      </CardHeader>
      <CardContent>
        {nothing ? (
          <p className="text-sm text-muted-foreground">
            {loading ? 'Loading…' : 'Nothing is waiting on you right now.'}
          </p>
        ) : (
          <ul className="space-y-2" aria-label="Waiting on you">
            {toAnswer > 0 ? (
              <WaitRow to={`${base}/questions`} count={toAnswer}>
                {toAnswer === 1 ? 'Buyer question to answer' : 'Buyer questions to answer'}
              </WaitRow>
            ) : null}
            {toFile > 0 ? (
              <WaitRow to={`${base}/processing?status=needs_filing`} count={toFile}>
                {toFile === 1 ? 'Document needs filing' : 'Documents need filing'}
              </WaitRow>
            ) : null}
            {couldNotFile > 0 ? (
              <WaitRow to={`${base}/processing?status=failed`} count={couldNotFile}>
                {couldNotFile === 1 ? 'Document we couldn’t file' : 'Documents we couldn’t file'}
              </WaitRow>
            ) : null}
            {votes > 0 ? (
              <WaitRow to={`${base}/lois`} count={votes}>
                {votes === 1 ? 'Vote waiting on you' : 'Votes waiting on you'}
              </WaitRow>
            ) : null}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Exclusivity, for the OWNERS (who see it on the LOIs tab): the countdown while a
 * period is in effect, or the note that an accepted LOI waits on their vote.
 * Nothing when there is none.
 */
export function ExclusivityCard({ roomId }: { roomId: string }) {
  const view = useRoomExclusivity(roomId).data;
  if (!view || view.state === 'none' || !view.exclusivity) return null;
  const extension = view.exclusivity.pendingExtensionDays;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-1.5">
          <CardTitle>Exclusivity</CardTitle>
          <HelpTip id="seller.dashboard.exclusivity-card" />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {view.state === 'in_effect' ? (
          <>
            {view.endsAt && view.expiresOn ? (
              <ExclusivityCountdown
                endsAt={view.endsAt}
                expiresOn={view.expiresOn}
                timezone={view.timezone}
              />
            ) : null}
            <p className="text-sm text-muted-foreground">
              A buyer holds exclusivity. Every other buyer’s data room access is paused until
              closing or the period ends.
            </p>
            {extension !== null && extension !== undefined ? (
              <p className="text-sm font-medium">
                An extension of {extension} days is waiting for the owners’ vote.
              </p>
            ) : null}
          </>
        ) : (
          <p className="text-sm font-medium">
            An accepted LOI is waiting for the owners’ vote. No buyer is paused until it is
            approved.
          </p>
        )}
        <Link to={`/rooms/${roomId}/lois`} className={linkClass}>
          Open the LOIs
        </Link>
      </CardContent>
    </Card>
  );
}

/** Processing: the estimate and per-stage tally, only while documents remain to process. */
export function ProcessingCard({ roomId }: { roomId: string }) {
  const estimate = useProcessingEstimate(roomId);
  if (!isProcessingEstimate(estimate.data) || estimate.data.documentsLeft <= 0) return null;
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex items-center gap-1.5">
            <CardTitle>Processing</CardTitle>
            <HelpTip id="seller.processing.progress-panel" />
          </div>
          <Link to={`/rooms/${roomId}/processing`} className={linkClass}>
            Processing status
          </Link>
        </div>
      </CardHeader>
      <CardContent>
        <ProcessingProgress estimate={estimate.data} />
      </CardContent>
    </Card>
  );
}
